import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RemoteVideoTrack, RemoteAudioTrack, RemoteTrackPublication } from "twilio-video";
import { cn } from "@tellescope/ui";
import type { VideoParticipantProps } from "../types";

/**
 * VideoParticipant - Renders a single participant's video and audio.
 *
 * Handles track subscription and attaches video/audio tracks to DOM elements.
 */
export function VideoParticipant({ participant, isLocal = false, resolveIdentity, className }: VideoParticipantProps) {
  const { t } = useTranslation("videoCalling");
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [videoTrack, setVideoTrack] = useState<RemoteVideoTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<RemoteAudioTrack | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const displayName = resolveIdentity?.(participant.identity) ?? participant.identity;

  // Subscribe to existing and new tracks
  useEffect(() => {
    const handleTrackSubscribed = (track: RemoteVideoTrack | RemoteAudioTrack) => {
      if (track.kind === "video") {
        setVideoTrack(track as RemoteVideoTrack);
      } else if (track.kind === "audio") {
        setAudioTrack(track as RemoteAudioTrack);
      }
    };

    const handleTrackUnsubscribed = (track: RemoteVideoTrack | RemoteAudioTrack) => {
      if (track.kind === "video") {
        setVideoTrack(null);
      } else if (track.kind === "audio") {
        setAudioTrack(null);
      }
    };

    const handleTrackEnabled = (publication: RemoteTrackPublication) => {
      if (publication.kind === "video") {
        setIsVideoEnabled(true);
      }
    };

    const handleTrackDisabled = (publication: RemoteTrackPublication) => {
      if (publication.kind === "video") {
        setIsVideoEnabled(false);
      }
    };

    // Get existing subscribed tracks
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed && publication.track) {
        handleTrackSubscribed(publication.track as RemoteVideoTrack | RemoteAudioTrack);
      }
    });

    // Subscribe to track events
    participant.on("trackSubscribed", handleTrackSubscribed);
    participant.on("trackUnsubscribed", handleTrackUnsubscribed);
    participant.on("trackEnabled", handleTrackEnabled);
    participant.on("trackDisabled", handleTrackDisabled);

    return () => {
      participant.off("trackSubscribed", handleTrackSubscribed);
      participant.off("trackUnsubscribed", handleTrackUnsubscribed);
      participant.off("trackEnabled", handleTrackEnabled);
      participant.off("trackDisabled", handleTrackDisabled);
    };
  }, [participant]);

  // Attach video track to video element
  useEffect(() => {
    if (videoTrack && videoRef.current) {
      videoTrack.attach(videoRef.current);
      return () => {
        videoTrack.detach(videoRef.current!);
      };
    }
  }, [videoTrack]);

  // Attach audio track to audio element (not for local participant)
  useEffect(() => {
    if (audioTrack && audioRef.current && !isLocal) {
      audioTrack.attach(audioRef.current);
      return () => {
        audioTrack.detach(audioRef.current!);
      };
    }
  }, [audioTrack, isLocal]);

  return (
    <div className={cn("relative w-full h-full bg-gray-900 rounded-lg overflow-hidden", className)}>
      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        muted={isLocal} // Mute local to prevent echo
        playsInline
        className={cn(
          "w-full h-full object-cover",
          isLocal && "scale-x-[-1]", // Mirror local video
          !isVideoEnabled && "hidden"
        )}
      />

      {/* Audio element (for remote participants only) */}
      {!isLocal && <audio ref={audioRef} autoPlay />}

      {/* Placeholder when video is disabled */}
      {!isVideoEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="relative w-1/2 max-w-[200px] aspect-square">
            <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center shadow-lg">
              <span className="text-[4rem] text-gray-700 font-medium select-none">
                {displayName?.charAt(0)?.toUpperCase() ?? "?"}
              </span>
            </div>
            {/* Online status indicator */}
            <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800" />
          </div>
        </div>
      )}

      {/* Participant label */}
      <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-white text-sm">
        {isLocal ? t("You") : displayName}
      </div>
    </div>
  );
}
